// Connected · EntityLink
//
// Clickable entity reference with a hover popover summary card.
// Composes presentational primitives:
//   - `@ds/display/EntityChip` — visual chip (icon · label · arrow), both pill and inline variants.
//   - `@ds/display/Icon`        — entity-type icon library (locked behind one wrapper).
//   - `@ds/display/Hairline`    — popover separator above the footer.
//   - `@ds/connected/EntitySummaryCard` — the popover body.
//   - `@ds/connected/OpenInTabButton`   — open-in-tab footer button.
//
// Owns the data-aware navigation lifecycle. The chip root renders as a real
// <a href> so ctrl/cmd+click and middle-click open the entity in a new tab.
// All visual values come from `@ds/tokens/palette`.
//
// API:
//   <EntityLink type="company" id={42} label="Acme Sp. z o.o." />
//   <EntityLink type="invoice" id={7}  label="2024/01/0001"   variant="inline" />

import React, { useMemo, useState } from "react";
import { Popover } from "antd";
import {
  EntityChip,
  type EntityChipInput,
} from "@ds/display/EntityChip";
import { Icon, type IconName } from "@ds/display/Icon";
import { Hairline } from "@ds/display/Hairline";
import { Inline } from "@ds/layout/Inline";
import { SPACE } from "@ds/tokens/palette";
import { type EntityType, getEntityRoute, absoluteAppUrl } from "@ds/navigation/navigation";
import { useNavigateWithCrumb } from "@ds/navigation/navTrail";
import { EntitySummaryCard } from "./EntitySummaryCard";
import { withExportSpec } from "@ds/display/exportSpec";
import { OpenInTabButton } from "./OpenInTabButton";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface EntityLinkInput {
  /** Entity kind — drives icon and routing. */
  type: EntityType;
  /** Entity numeric primary key. */
  id: number;
  /** Display label (entity short name, reference number, etc). */
  label: React.ReactNode;
  /**
   * Visual variant.
   * - `"inline"` (default) — blue text + small trailing arrow, no chrome.
   *   The canonical look across the app: KV values, table cells, peek
   *   summaries, prose, inline editables.
   * - `"pill"` — soft-blue chip with icon · label · trailing arrow.
   *   Reserved for `PartyCard`'s entity-name affordance; other consumers
   *   should not opt in.
   */
  variant?: "pill" | "inline";
  /** Size scale. Default `md`. Only applies in chip/pill variant. */
  size?: "sm" | "md";
  /** Inline style escape hatch — applied to the chip root. */
  style?: React.CSSProperties;
  /** className escape hatch — applied to the chip root. */
  className?: string;
  /** `data-testid` applied to the chip root via EntityChip. */
  testId?: string;
}

// ─── Component contract ──────────────────────────────────────────────────
// Connected component: no Outputs (navigation is owned internally), no Actions.

export type EntityLinkProps = EntityLinkInput;

// ─── Per-EntityType icon name (mapped to @ds/display/Icon) ───────────────

const ENTITY_ICON_NAME: Record<EntityType, IconName> = {
  company:             "shop",
  product:             "inbox",
  transaction:         "swap",
  transaction_v2:      "swap",
  price_list:          "creditCard",
  invoice:             "fileText",
  invoice_v2:          "fileText",
  invoice_reconcile:   "fileText",
  employee:            "user",
  edpr_document:       "fileDone",
  edpr_request:        "search",
  bank_batch:          "bank",
  bank_statement_line: "bank",
  commission_payee:    "user",
  commission_rule:     "settings",
};

// ─── Implementation ──────────────────────────────────────────────────────

export const EntityLink: React.FC<EntityLinkProps> = ({
  type,
  id,
  label,
  variant = "inline",
  size,
  style,
  className,
  testId,
}) => {
  const navigateWithCrumb = useNavigateWithCrumb();
  const [open, setOpen] = useState(false);

  const href = getEntityRoute(type, id);

  // ── Popover footer ───────────────────────────────────────────────────────
  // OpenInTabButton routes through the entity navigation seam.
  //
  // Memoized to avoid re-constructing the React element tree (and re-mounting
  // EntitySummaryCard, which triggers a Refine `useOne` against the ambient
  // dataProvider) on every parent render. AntD `Popover` already unmounts the
  // body when closed via `destroyOnHidden`, so re-creating the element on every
  // render isn't free even when the popover is hidden — React still walks the
  // tree to compare. Only re-derive when the actual inputs change.
  const popoverContent = useMemo(() => (
    <div>
      <EntitySummaryCard type={type} id={id} />
      <Hairline spacing={SPACE.sm} />
      <Inline justify="between" align="center">
        <OpenInTabButton type={type} id={id} />
      </Inline>
    </div>
  ), [type, id]);

  // ── Click handler — plain left-click SPA-navigates; all others pass through
  // to the native anchor so ctrl/cmd+click and middle-click open a new tab.
  const handleClick = (e: React.MouseEvent) => {
    // Non-plain click (modifier key or non-primary button): let the native
    // anchor handle it (opens new tab / window). Do NOT preventDefault.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    // Plain left-click: intercept and SPA-navigate to keep the breadcrumb trail.
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    navigateWithCrumb(href);
  };

  // Map EntityLink's legacy "pill" variant to EntityChip's "chip" variant.
  // EntityLink preserves "pill" in its public API for backward compat with consumers
  // that were wired up before EntityChip was extracted.
  const chipVariant: "chip" | "inline" = variant === "pill" ? "chip" : "inline";

  // EntityChip props shared by both variants
  const chipProps: EntityChipInput & { onClick: (e: React.MouseEvent) => void; href: string } = {
    label,
    variant: chipVariant,
    tone:    "blue",
    icon:    variant === "pill" ? <Icon name={ENTITY_ICON_NAME[type]} size="sm" /> : undefined,
    arrow:   true,
    size,
    testId,
    href,
    onClick: handleClick,
  };

  return (
    <Popover
      content={popoverContent}
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
      }}
      trigger="hover"
      mouseEnterDelay={0.15}
      mouseLeaveDelay={0.15}
      placement="rightTop"
      destroyOnHidden
      overlayStyle={{ maxWidth: 360 }}
      styles={{ body: { padding: `${SPACE.sm}px ${SPACE.md}px` } }}
    >
      {/* Wrapper span so AntD Popover can attach its trigger ref without
          interfering with EntityChip's own root testId/click handling. */}
      <span style={{ display: "inline-flex", ...style }} className={className}>
        <EntityChip {...chipProps} />
      </span>
    </Popover>
  );
};

// ─── Excel export ─────────────────────────────────────────────────────────
//
// An entity reference exports as a real clickable hyperlink instead of a bare
// label, so a row in a workbook still leads back to the record it describes.
// Excel needs an absolute URL, so the app route is resolved against the origin
// the export was made from — a file exported from prod links to prod, one made
// on dev links to dev.
//
// `label` is a ReactNode. When it isn't a plain string or number we can't
// derive a cell value here; returning undefined hands the cell back to the
// exporter's shadow-DOM fallback, which reads the rendered text.

withExportSpec<typeof EntityLink, EntityLinkProps>(EntityLink, {
  type: "link",
  value: (p) => (typeof p.label === "string" || typeof p.label === "number" ? p.label : undefined),
  hyperlink: (p) =>
    p.type != null && p.id != null ? absoluteAppUrl(getEntityRoute(p.type, p.id)) : undefined,
});
